import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';

import { Link } from '@/components/shared/element/link';
import { Text } from '@/components/shared/element/text';
import { Box } from '@/components/shared/layout/box';
import { Container } from '@/components/shared/layout/container';
import { styled } from '@/stitches.config';

const TabItem = styled(Link, {
  position: 'relative',
  display: 'block',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: '$space-0',
    bottom: '$space-0',
    width: '100%',
    height: '$size-0-5',
    backgroundColor: '$gray400',
    pointerEvents: 'none',
    transition:
      'background-color var(--transition-duration) var(--transition-easing)',
  },
  '@hover': {
    '&:hover': {
      '&::after': {
        backgroundColor: '$gray700',
      },
    },
  },
  '&.is-current': {
    color: '$primary',
    '&::after': {
      backgroundColor: '$primary',
    },
  },
});

const TabList = styled(Box, {
  '& .swiper-slide': {
    width: 'auto',
  },
});

const TabLine = styled(Box, {
  height: '1px',
  transition: 'opacity var(--transition-duration) var(--transition-easing)',
});

interface Category {
  name: string;
  url: string;
  active: boolean;
}

interface TabsProps {
  category: Category[];
  dataTrack?: string;
  dataTest?: string;
}

const renderTabItem = (
  item: Category,
  index: number,
  dataTrack: string,
  dataTest: string
) => (
  <TabItem
    key={index}
    href={item.url}
    tracking={{
      dataTrack: `${dataTrack}-tab`,
      dataTrackText: item.name,
      dataTrackUrl: item.url,
    }}
    test={{
      dataTest: `${dataTest}_link`,
    }}
    className={item.active ? 'is-current' : ''}
  >
    <Box
      paddingX="6"
      paddingY="3"
      backgroundColor="white"
      height="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      css={{
        whiteSpace: 'nowrap',
        minWidth: '$size-28',
        minHeight: '$size-14',
        '@md': {
          minWidth: '$size-32',
          minHeight: '$size-16',
        },
      }}
    >
      <Text
        as="h2"
        size="text-body-2"
        font="bold"
        variant={item.active ? 'text-red' : undefined}
        data-test={`${dataTest}_title`}
      >
        {item.name}
      </Text>
    </Box>
  </TabItem>
);

export const Tabs: React.FC<TabsProps> = ({
  category = [],
  dataTrack = 'offer',
  dataTest = 'tab',
}: TabsProps) => {
  const selectedIndex = category.findIndex((item) => item.active);

  return (
    <Box
      position="sticky"
      top="sticky-top"
      backgroundColor="white"
      zIndex="2"
      test={{
        dataTest: `${dataTest}s`,
      }}
    >
      <Container size="desktop-only">
        <TabList position="relative">
          <TabLine
            position="absolute"
            left="0"
            bottom="0"
            width="full"
            backgroundColor="gray300"
            pointerEvents="none"
          />
          <Swiper
            loop={false}
            slidesPerView="auto"
            centeredSlides={false}
            initialSlide={selectedIndex >= 0 ? selectedIndex : 0}
          >
            {category.map((categoryItem, index) => (
              <SwiperSlide key={index} data-test={`${dataTest}_list_${index}`}>
                {renderTabItem(categoryItem, index, dataTrack, dataTest)}
              </SwiperSlide>
            ))}
          </Swiper>
        </TabList>
      </Container>
    </Box>
  );
};

Tabs.displayName = 'Tabs';
